/**
 * Wave 2A — Bank Statements UI (Feb 2026).
 *
 * Encapsulates the Statements tab so Bank.js stays manageable. Two
 * views:
 *
 *   1. List view  — card grid of all statements with quick KPIs and
 *                   filters (account / status).
 *   2. Detail view — header info + lines table + attachments + add-
 *                    line form + add-attachment uploader.
 *
 * The component speaks only to the new endpoints under
 * /bank/statements/*, never directly to bank_transactions.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import {
  FileText, Plus, ChevronLeft, Upload, Paperclip, X,
  Lock, Archive, Ban, ArrowUpCircle, ArrowDownCircle, Clock,
} from 'lucide-react';
import { axiosInstance } from '../App';

// ─── helpers ────────────────────────────────────────────────────────
const STATUS_STYLES = {
  draft:       'bg-gray-100 text-gray-700 border-gray-200',
  imported:    'bg-blue-50 text-blue-700 border-blue-200',
  reconciling: 'bg-amber-50 text-amber-700 border-amber-200',
  reconciled:  'bg-green-50 text-green-700 border-green-200',
  locked:      'bg-purple-50 text-purple-700 border-purple-200',
  cancelled:   'bg-rose-50 text-rose-600 border-rose-200',
  archived:    'bg-slate-100 text-slate-500 border-slate-200',
};

const STATUS_LABELS_AR = {
  draft:       'مسودة',
  imported:    'مُستورد',
  reconciling: 'قيد المطابقة',
  reconciled:  'متطابق',
  locked:      'مُقفل',
  cancelled:   'مُلغى',
  archived:    'مُؤرشف',
};

const MATCH_STATUS_LABELS_AR = {
  unmatched: 'غير مطابق',
  partial:   'مطابقة جزئية',
  matched:   'مطابق',
  ignored:   'متجاهَل',
  cancelled: 'مُلغى',
};

const fmtMoney = (n) =>
  (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (s) => (s ? String(s).split('T')[0] : '—');

// ════════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════════
const BankStatementsTab = ({ accounts = [], isAdmin = false }) => {
  const [statements, setStatements] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filterAccount, setFilterAccount] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedStatement, setSelectedStatement] = useState(null);

  // ── List fetch ────────────────────────────────────────────────
  const fetchStatements = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterAccount) params.append('bank_account_id', filterAccount);
      if (filterStatus) params.append('status', filterStatus);
      const { data } = await axiosInstance.get(`/bank/statements?${params}`);
      setStatements(data || []);
    } catch (e) {
      console.error(e);
      toast.error('فشل تحميل كشوف الحسابات');
    } finally {
      setLoading(false);
    }
  }, [filterAccount, filterStatus]);

  useEffect(() => {
    if (selectedStatement) return;
    fetchStatements();
  }, [fetchStatements, selectedStatement]);

  // ── Detail view ───────────────────────────────────────────────
  if (selectedStatement) {
    return (
      <BankStatementDetail
        statementId={selectedStatement}
        onBack={() => setSelectedStatement(null)}
        onChanged={fetchStatements}
        isAdmin={isAdmin}
      />
    );
  }

  // ── List view ─────────────────────────────────────────────────
  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-testid="bank-statements-tab">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">كشوف الحسابات البنكية</h3>
          <p className="text-xs text-gray-500 mt-1">
            Bank Statements — رفع كشف الحساب من البنك واستخدامه كدليل للمطابقة
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <select
            data-testid="filter-statement-account"
            value={filterAccount}
            onChange={(e) => setFilterAccount(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg"
          >
            <option value="">كل الحسابات</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.bank_name} — {String(a.account_number).slice(-4)}
              </option>
            ))}
          </select>
          <select
            data-testid="filter-statement-status"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-3 py-2 text-sm border rounded-lg"
          >
            <option value="">كل الحالات</option>
            {Object.keys(STATUS_LABELS_AR).map((k) => (
              <option key={k} value={k}>{STATUS_LABELS_AR[k]}</option>
            ))}
          </select>
          <button
            data-testid="open-create-statement-modal"
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg font-medium"
          >
            <Plus className="w-4 h-4" />
            كشف جديد
          </button>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-gray-400 text-sm">جارٍ التحميل...</div>
      ) : statements.length === 0 ? (
        <div className="text-center py-16 text-gray-400" data-testid="statements-empty-state">
          <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
          <p className="text-sm">لا توجد كشوف بنكية بعد. ابدأ بإنشاء كشف جديد.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {statements.map((s) => (
            <button
              key={s.id}
              data-testid={`statement-card-${s.statement_no}`}
              onClick={() => setSelectedStatement(s.id)}
              className="text-right border border-gray-200 hover:border-blue-400 hover:shadow-sm transition rounded-xl p-4 bg-gradient-to-br from-white to-slate-50"
            >
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold text-gray-600">{s.statement_no}</span>
                <span className={`text-[10px] px-2 py-0.5 rounded-full border ${STATUS_STYLES[s.status] || ''}`}>
                  {STATUS_LABELS_AR[s.status] || s.status}
                </span>
              </div>
              <div className="mt-2 text-sm font-medium text-gray-800 truncate">
                {s.bank_account_name || '—'}
              </div>
              <div className="mt-1 text-xs text-gray-500">
                {fmtDate(s.start_date)} → {fmtDate(s.end_date)}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-green-50 rounded-md px-2 py-1.5">
                  <div className="text-green-700 font-medium">{fmtMoney(s.total_credit)}</div>
                  <div className="text-green-500/80">إيداعات</div>
                </div>
                <div className="bg-red-50 rounded-md px-2 py-1.5">
                  <div className="text-red-700 font-medium">{fmtMoney(s.total_debit)}</div>
                  <div className="text-red-500/80">مسحوبات</div>
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between text-[11px] text-gray-500">
                <span>{s.line_count || 0} سطر</span>
                <span>📎 {s.attachments?.length || 0}</span>
              </div>
            </button>
          ))}
        </div>
      )}

      {showCreateModal && (
        <CreateStatementModal
          accounts={accounts}
          onClose={() => setShowCreateModal(false)}
          onCreated={(id) => {
            setShowCreateModal(false);
            fetchStatements();
            setSelectedStatement(id);
          }}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Create modal
// ════════════════════════════════════════════════════════════════════
const CreateStatementModal = ({ accounts, onClose, onCreated }) => {
  const [form, setForm] = useState({
    bank_account_id: accounts[0]?.id || '',
    statement_date: new Date().toISOString().split('T')[0],
    bank_reference: '',
    start_date: '',
    end_date: '',
    opening_balance: 0,
    closing_balance: 0,
    currency: 'SAR',
    exchange_rate: '',
    base_currency: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.bank_account_id || !form.start_date || !form.end_date) {
      toast.error('الرجاء تعبئة الحقول الإلزامية');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        opening_balance: parseFloat(form.opening_balance) || 0,
        closing_balance: parseFloat(form.closing_balance) || 0,
        exchange_rate: form.exchange_rate ? parseFloat(form.exchange_rate) : null,
        bank_reference: form.bank_reference || null,
        base_currency: form.base_currency || null,
        notes: form.notes || null,
      };
      const { data } = await axiosInstance.post('/bank/statements', payload);
      toast.success(`تم إنشاء الكشف ${data.statement_no}`);
      onCreated(data.id);
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'فشل الإنشاء');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="create-statement-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">كشف بنكي جديد</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-create-statement"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-gray-700">الحساب البنكي *</span>
            <select
              required
              data-testid="statement-bank-account-select"
              value={form.bank_account_id}
              onChange={(e) => setForm({ ...form, bank_account_id: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            >
              <option value="">— اختر —</option>
              {accounts.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.bank_name} — {String(a.account_number).slice(-4)} ({a.currency})
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-gray-700">تاريخ إصدار الكشف</span>
            <input
              type="date"
              data-testid="statement-date-input"
              value={form.statement_date}
              onChange={(e) => setForm({ ...form, statement_date: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">مرجع البنك (Statement #)</span>
            <input
              type="text"
              data-testid="statement-bank-reference-input"
              value={form.bank_reference}
              onChange={(e) => setForm({ ...form, bank_reference: e.target.value })}
              placeholder="STMT-2026-Jan-001"
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">العملة</span>
            <input
              type="text"
              data-testid="statement-currency-input"
              value={form.currency}
              onChange={(e) => setForm({ ...form, currency: e.target.value.toUpperCase() })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">من تاريخ *</span>
            <input
              type="date"
              required
              data-testid="statement-start-date-input"
              value={form.start_date}
              onChange={(e) => setForm({ ...form, start_date: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">إلى تاريخ *</span>
            <input
              type="date"
              required
              data-testid="statement-end-date-input"
              value={form.end_date}
              onChange={(e) => setForm({ ...form, end_date: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">رصيد الافتتاح</span>
            <input
              type="number"
              step="0.01"
              data-testid="statement-opening-balance-input"
              value={form.opening_balance}
              onChange={(e) => setForm({ ...form, opening_balance: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">رصيد الإقفال</span>
            <input
              type="number"
              step="0.01"
              data-testid="statement-closing-balance-input"
              value={form.closing_balance}
              onChange={(e) => setForm({ ...form, closing_balance: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">سعر الصرف (اختياري)</span>
            <input
              type="number"
              step="0.0001"
              data-testid="statement-exchange-rate-input"
              value={form.exchange_rate}
              onChange={(e) => setForm({ ...form, exchange_rate: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">العملة الأساس (اختياري)</span>
            <input
              type="text"
              data-testid="statement-base-currency-input"
              value={form.base_currency}
              onChange={(e) => setForm({ ...form, base_currency: e.target.value.toUpperCase() })}
              placeholder="SAR"
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block md:col-span-2">
            <span className="text-gray-700">ملاحظات</span>
            <textarea
              data-testid="statement-notes-input"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={2}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg" data-testid="cancel-create-statement">إلغاء</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-create-statement"
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? 'جارٍ الحفظ...' : 'إنشاء الكشف'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Detail view
// ════════════════════════════════════════════════════════════════════
const BankStatementDetail = ({ statementId, onBack, onChanged, isAdmin }) => {
  const [stmt, setStmt] = useState(null);
  const [lines, setLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddLine, setShowAddLine] = useState(false);
  const fileRef = useRef(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [{ data: s }, { data: ls }] = await Promise.all([
        axiosInstance.get(`/bank/statements/${statementId}`),
        axiosInstance.get(`/bank/statements/${statementId}/lines`),
      ]);
      setStmt(s);
      setLines(ls || []);
    } catch (e) {
      toast.error('تعذر تحميل تفاصيل الكشف');
    } finally {
      setLoading(false);
    }
  }, [statementId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const editable = useMemo(() => {
    if (!stmt) return false;
    return ['draft', 'imported', 'reconciling'].includes(stmt.status);
  }, [stmt]);

  const uploadFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      await axiosInstance.post(`/bank/statements/${statementId}/attachments`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('تم رفع الملف');
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل رفع الملف');
    } finally {
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm('إزالة هذا المرفق؟ سيتم تسجيل العملية في الـ Audit Trail.')) return;
    try {
      await axiosInstance.delete(`/bank/statements/${statementId}/attachments/${attId}`);
      toast.success('تم إزالة المرفق');
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر إزالة المرفق');
    }
  };

  const cancelStatement = async () => {
    const reason = window.prompt('سبب إلغاء الكشف؟');
    if (reason === null) return;
    try {
      await axiosInstance.post(`/bank/statements/${statementId}/cancel`, { reason });
      toast.success('تم إلغاء الكشف');
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر الإلغاء');
    }
  };

  const archiveStatement = async () => {
    if (!window.confirm('أرشفة هذا الكشف؟')) return;
    try {
      await axiosInstance.post(`/bank/statements/${statementId}/archive`);
      toast.success('تمت الأرشفة');
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر الأرشفة');
    }
  };

  const lockStatement = async () => {
    if (!window.confirm('قفل الكشف؟ لن يكون قابلاً للتعديل بعدها.')) return;
    try {
      await axiosInstance.post(`/bank/statements/${statementId}/lock`);
      toast.success('تم قفل الكشف');
      fetchAll();
      onChanged?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر القفل');
    }
  };

  const cancelLine = async (line) => {
    const reason = window.prompt('سبب إلغاء هذا السطر؟');
    if (reason === null) return;
    try {
      await axiosInstance.post(`/bank/statements/${statementId}/lines/${line.id}/cancel`, { reason });
      toast.success('تم إلغاء السطر');
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'تعذر الإلغاء');
    }
  };

  if (loading || !stmt) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-8 text-center text-gray-400">
        جارٍ التحميل...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="bank-statement-detail">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3">
            <button
              onClick={onBack}
              data-testid="back-to-statements-list"
              className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-lg font-semibold text-gray-800">{stmt.statement_no}</h3>
                <span className={`text-xs px-2 py-0.5 rounded-full border ${STATUS_STYLES[stmt.status] || ''}`}>
                  {STATUS_LABELS_AR[stmt.status] || stmt.status}
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {stmt.bank_account_name} · {fmtDate(stmt.start_date)} → {fmtDate(stmt.end_date)} · {stmt.currency}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {editable && (
              <button
                onClick={cancelStatement}
                data-testid="cancel-statement-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-rose-200 text-rose-700 hover:bg-rose-50 rounded-lg"
              >
                <Ban className="w-3.5 h-3.5" />
                إلغاء الكشف
              </button>
            )}
            {isAdmin && stmt.status !== 'locked' && stmt.status !== 'archived' && (
              <button
                onClick={lockStatement}
                data-testid="lock-statement-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-purple-200 text-purple-700 hover:bg-purple-50 rounded-lg"
              >
                <Lock className="w-3.5 h-3.5" />
                قفل
              </button>
            )}
            {isAdmin && stmt.status !== 'archived' && (
              <button
                onClick={archiveStatement}
                data-testid="archive-statement-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs border border-slate-200 text-slate-700 hover:bg-slate-50 rounded-lg"
              >
                <Archive className="w-3.5 h-3.5" />
                أرشفة
              </button>
            )}
          </div>
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mt-4 text-sm">
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500">رصيد افتتاحي</div>
            <div className="font-semibold text-gray-800 mt-1">{fmtMoney(stmt.opening_balance)}</div>
          </div>
          <div className="bg-green-50 rounded-lg p-3">
            <div className="text-xs text-green-600">إجمالي الإيداعات</div>
            <div className="font-semibold text-green-800 mt-1">{fmtMoney(stmt.total_credit)}</div>
          </div>
          <div className="bg-red-50 rounded-lg p-3">
            <div className="text-xs text-red-600">إجمالي المسحوبات</div>
            <div className="font-semibold text-red-800 mt-1">{fmtMoney(stmt.total_debit)}</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-3">
            <div className="text-xs text-slate-500">رصيد إقفال</div>
            <div className="font-semibold text-gray-800 mt-1">{fmtMoney(stmt.closing_balance)}</div>
          </div>
          <div className="bg-blue-50 rounded-lg p-3">
            <div className="text-xs text-blue-600">المطابقة</div>
            <div className="font-semibold text-blue-800 mt-1">
              {stmt.matched_count || 0} / {stmt.line_count || 0}
            </div>
          </div>
        </div>

        {/* Audit footer */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mt-4 text-[11px] text-gray-500">
          <div>
            <span className="font-medium text-gray-600">أنشأه:</span>{' '}
            {stmt.created_by_name || '—'} · {fmtDate(stmt.created_at)}
          </div>
          {stmt.modified_by_name && (
            <div>
              <span className="font-medium text-gray-600">آخر تعديل:</span>{' '}
              {stmt.modified_by_name} · {fmtDate(stmt.modified_at)}
            </div>
          )}
          {stmt.locked_by_name && (
            <div>
              <span className="font-medium text-purple-600">مُقفل بواسطة:</span>{' '}
              {stmt.locked_by_name} · {fmtDate(stmt.locked_at)}
            </div>
          )}
          {stmt.reconciled_by_name && (
            <div>
              <span className="font-medium text-green-700">طابقه:</span>{' '}
              {stmt.reconciled_by_name} · {fmtDate(stmt.reconciled_at)}
            </div>
          )}
          {stmt.cancelled_by_name && (
            <div>
              <span className="font-medium text-rose-700">ألغاه:</span>{' '}
              {stmt.cancelled_by_name} · {fmtDate(stmt.cancelled_at)}
              {stmt.cancel_reason ? ` · "${stmt.cancel_reason}"` : ''}
            </div>
          )}
        </div>
      </div>

      {/* Attachments */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-800 flex items-center gap-2">
            <Paperclip className="w-4 h-4 text-gray-400" />
            المرفقات ({stmt.attachments?.length || 0})
          </h4>
          {editable && (
            <>
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.csv,.xlsx,.xls,.png,.jpg,.jpeg,.gif,.webp"
                onChange={uploadFile}
                className="hidden"
                data-testid="upload-statement-file-input"
              />
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="upload-statement-file-btn"
                className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
              >
                <Upload className="w-3.5 h-3.5" />
                رفع ملف (PDF / Excel / CSV)
              </button>
            </>
          )}
        </div>
        {stmt.attachments && stmt.attachments.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {stmt.attachments.map((att) => (
              <div key={att.id} className="flex items-center justify-between border rounded-lg p-3 text-xs">
                <div className="min-w-0">
                  <div className="font-medium text-gray-800 truncate">{att.file_name}</div>
                  <div className="text-gray-500 mt-0.5">
                    {att.kind} · {(att.file_size / 1024).toFixed(1)} KB · SHA: {String(att.file_hash).slice(0, 10)}…
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={att.file_url}
                    target="_blank"
                    rel="noreferrer"
                    className="text-blue-600 hover:underline"
                    data-testid={`open-attachment-${att.id}`}
                  >
                    فتح
                  </a>
                  {editable && (
                    <button
                      onClick={() => removeAttachment(att.id)}
                      className="text-rose-600 hover:underline"
                      data-testid={`remove-attachment-${att.id}`}
                    >
                      إزالة
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400">لا توجد مرفقات بعد.</p>
        )}
      </div>

      {/* Lines */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-medium text-gray-800">سطور الكشف ({lines.length})</h4>
          {editable && (
            <button
              onClick={() => setShowAddLine(true)}
              data-testid="open-add-line"
              className="inline-flex items-center gap-1 px-3 py-2 text-xs bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              <Plus className="w-3.5 h-3.5" />
              إضافة سطر يدوياً
            </button>
          )}
        </div>
        {lines.length === 0 ? (
          <p className="text-xs text-gray-400">لا توجد سطور بعد. أضف يدوياً أو استخدم استيراد CSV/Excel (Wave 2B).</p>
        ) : (
          <div className="overflow-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 bg-slate-50">
                <tr>
                  <th className="px-2 py-2 text-right">#</th>
                  <th className="px-2 py-2 text-right">التاريخ</th>
                  <th className="px-2 py-2 text-right">الوصف</th>
                  <th className="px-2 py-2 text-right">المرجع</th>
                  <th className="px-2 py-2 text-right">الطرف الآخر</th>
                  <th className="px-2 py-2 text-right">القناة</th>
                  <th className="px-2 py-2 text-right">المبلغ</th>
                  <th className="px-2 py-2 text-right">الحالة</th>
                  <th className="px-2 py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {lines.map((ln) => (
                  <tr key={ln.id} className="border-t" data-testid={`statement-line-${ln.line_no}`}>
                    <td className="px-2 py-2 text-gray-500">{ln.line_no}</td>
                    <td className="px-2 py-2">{fmtDate(ln.line_date)}</td>
                    <td className="px-2 py-2 text-gray-800">{ln.description || '—'}</td>
                    <td className="px-2 py-2 text-gray-500">{ln.bank_reference || '—'}</td>
                    <td className="px-2 py-2 text-gray-700">{ln.counterparty_name || '—'}</td>
                    <td className="px-2 py-2 text-gray-500">{ln.channel || '—'}</td>
                    <td className="px-2 py-2 font-medium">
                      <span className={ln.type === 'credit' ? 'text-green-700' : 'text-red-700'}>
                        {ln.type === 'credit' ? (
                          <ArrowDownCircle className="inline w-3.5 h-3.5 mr-1" />
                        ) : (
                          <ArrowUpCircle className="inline w-3.5 h-3.5 mr-1" />
                        )}
                        {fmtMoney(ln.amount)}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-700">
                        {MATCH_STATUS_LABELS_AR[ln.match_status] || ln.match_status}
                      </span>
                    </td>
                    <td className="px-2 py-2">
                      {editable && ln.match_status !== 'cancelled' && (
                        <button
                          onClick={() => cancelLine(ln)}
                          data-testid={`cancel-line-${ln.line_no}`}
                          className="text-rose-600 hover:underline text-[11px]"
                        >
                          إلغاء
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showAddLine && (
        <AddLineModal
          statementId={statementId}
          onClose={() => setShowAddLine(false)}
          onCreated={() => {
            setShowAddLine(false);
            fetchAll();
          }}
        />
      )}
    </div>
  );
};

// ════════════════════════════════════════════════════════════════════
// Add manual line modal
// ════════════════════════════════════════════════════════════════════
const AddLineModal = ({ statementId, onClose, onCreated }) => {
  const [form, setForm] = useState({
    line_date: new Date().toISOString().split('T')[0],
    value_date: '',
    transaction_date: '',
    description: '',
    bank_reference: '',
    customer_reference: '',
    amount: '',
    type: 'credit',
    balance_after: '',
    channel: '',
    counterparty_name: '',
    counterparty_iban: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.line_date || !form.amount) {
      toast.error('التاريخ والمبلغ إلزاميان');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        amount: parseFloat(form.amount),
        balance_after: form.balance_after ? parseFloat(form.balance_after) : null,
        value_date: form.value_date || null,
        transaction_date: form.transaction_date || null,
        channel: form.channel || null,
        description: form.description || null,
        bank_reference: form.bank_reference || null,
        customer_reference: form.customer_reference || null,
        counterparty_name: form.counterparty_name || null,
        counterparty_iban: form.counterparty_iban || null,
        notes: form.notes || null,
      };
      await axiosInstance.post(`/bank/statements/${statementId}/lines`, payload);
      toast.success('تمت إضافة السطر');
      onCreated();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'فشل الإضافة');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="add-line-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-auto">
        <div className="px-5 py-4 border-b flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">إضافة سطر يدوي</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" data-testid="close-add-line"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <span className="text-gray-700">التاريخ *</span>
            <input
              type="date" required
              data-testid="line-date"
              value={form.line_date}
              onChange={(e) => setForm({ ...form, line_date: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">المبلغ *</span>
            <input
              type="number" step="0.01" required
              data-testid="line-amount"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">النوع *</span>
            <select
              data-testid="line-type"
              value={form.type}
              onChange={(e) => setForm({ ...form, type: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            >
              <option value="credit">إيداع (Credit)</option>
              <option value="debit">سحب (Debit)</option>
            </select>
          </label>
          <label className="block">
            <span className="text-gray-700">القناة</span>
            <select
              data-testid="line-channel"
              value={form.channel}
              onChange={(e) => setForm({ ...form, channel: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            >
              <option value="">—</option>
              <option value="atm">ATM</option>
              <option value="pos">POS</option>
              <option value="online">Online</option>
              <option value="transfer">Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="cash">Cash</option>
              <option value="fee">Fee</option>
              <option value="interest">Interest</option>
              <option value="salary">Salary</option>
              <option value="other">Other</option>
            </select>
          </label>
          <label className="block md:col-span-2">
            <span className="text-gray-700">الوصف</span>
            <input
              type="text"
              data-testid="line-description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-1 w-full px-3 py-2 border rounded-lg"
            />
          </label>
          <label className="block">
            <span className="text-gray-700">تاريخ القيمة (Value Date)</span>
            <input type="date" data-testid="line-value-date" value={form.value_date} onChange={(e) => setForm({ ...form, value_date: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">تاريخ المعاملة</span>
            <input type="date" data-testid="line-tx-date" value={form.transaction_date} onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">مرجع البنك</span>
            <input type="text" data-testid="line-bank-ref" value={form.bank_reference} onChange={(e) => setForm({ ...form, bank_reference: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">مرجع العميل</span>
            <input type="text" data-testid="line-cust-ref" value={form.customer_reference} onChange={(e) => setForm({ ...form, customer_reference: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">الطرف الآخر</span>
            <input type="text" data-testid="line-counterparty" value={form.counterparty_name} onChange={(e) => setForm({ ...form, counterparty_name: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">IBAN الطرف الآخر</span>
            <input type="text" data-testid="line-iban" value={form.counterparty_iban} onChange={(e) => setForm({ ...form, counterparty_iban: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block">
            <span className="text-gray-700">الرصيد بعد العملية</span>
            <input type="number" step="0.01" data-testid="line-balance" value={form.balance_after} onChange={(e) => setForm({ ...form, balance_after: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>
          <label className="block md:col-span-2">
            <span className="text-gray-700">ملاحظات</span>
            <textarea rows={2} data-testid="line-notes" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1 w-full px-3 py-2 border rounded-lg" />
          </label>

          <div className="md:col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm border rounded-lg" data-testid="cancel-add-line">إلغاء</button>
            <button
              type="submit"
              disabled={saving}
              data-testid="submit-add-line"
              className="px-4 py-2 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium disabled:opacity-50"
            >
              {saving ? 'جارٍ الحفظ...' : 'إضافة السطر'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankStatementsTab;
